import { AlertCircle, Eye, EyeOff, Loader2 } from 'lucide-react';
import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { PasswordRequirementsChecklist } from '@/components/auth/PasswordRequirementsChecklist';
import { PasswordStrengthMeter } from '@/components/auth/PasswordStrengthMeter';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ApiError, NetworkError } from '@/lib/api-client';
import { isStrongPassword } from '@/lib/password-rules';
import { useAuthStore } from '@/stores/authStore';
import { STRINGS } from '@/strings/strings';

type FormError = 'invalidToken' | 'generic' | 'network';

// Backend AuthService.resetPassword throws `BadRequestException(
// 'Invalid or expired reset token')` when the row is missing,
// revoked, or expired. DreamableExceptionFilter wraps every 400 as
// `validation_error` with the exception message as user_message —
// so this exact English string identifies the token-level failure.
// Any OTHER 400 user_message is a class-validator password-rule
// error (German, from ResetPasswordDto's @Matches decorators).
const BACKEND_INVALID_TOKEN_MESSAGE = 'Invalid or expired reset token';

export function ResetPasswordRoute() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const navigate = useNavigate();
  const confirmPasswordReset = useAuthStore((s) => s.confirmPasswordReset);

  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordConfirmError, setPasswordConfirmError] = useState<string | null>(null);
  const [formError, setFormError] = useState<FormError | null>(() =>
    token ? null : 'invalidToken',
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Mirrors the backend ResetPasswordDto `@Matches` chain so a
  // password that fails any of the three rules is caught before the
  // network round-trip. See `@/lib/password-rules` for the shared
  // regex source.
  const validatePassword = (value: string): string | null => {
    if (!value.trim()) return STRINGS.auth.errors.passwordRequired;
    if (value.length < 8) return STRINGS.auth.errors.passwordTooShort;
    if (!isStrongPassword(value)) {
      return STRINGS.auth.errors.passwordRequirementsNotMet;
    }
    return null;
  };

  const validateConfirm = (value: string, target: string): string | null => {
    if (!value) return STRINGS.auth.errors.passwordConfirmRequired;
    if (value !== target) return STRINGS.auth.errors.passwordMismatch;
    return null;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!token) {
      setFormError('invalidToken');
      return;
    }

    const pwErr = validatePassword(password);
    const confirmErr = validateConfirm(passwordConfirm, password);
    setPasswordError(pwErr);
    setPasswordConfirmError(confirmErr);
    if (pwErr || confirmErr) return;

    setFormError(null);
    setIsSubmitting(true);
    try {
      await confirmPasswordReset({ token, newPassword: password });
      navigate('/auth/login', { replace: true });
    } catch (err) {
      if (err instanceof NetworkError) {
        setFormError('network');
      } else if (err instanceof ApiError && err.httpStatus === 400) {
        // The service-level token rejection and the DTO-level
        // password-rule rejections both arrive as HTTP 400. Keep
        // the "Neuen Link anfordern" page reserved for the real
        // token failure; surface password-rule messages inline so
        // the user keeps the token in the URL and can retry.
        const envelopeMessage = err.envelope?.user_message ?? '';
        if (envelopeMessage === BACKEND_INVALID_TOKEN_MESSAGE) {
          setFormError('invalidToken');
        } else if (envelopeMessage) {
          setPasswordError(envelopeMessage);
        } else {
          setFormError('generic');
        }
      } else {
        setFormError('generic');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (formError === 'invalidToken') {
    return (
      <>
        <h1 className="text-[22px] font-semibold text-dream-white mb-4">
          {STRINGS.auth.reset.title}
        </h1>
        <div
          role="alert"
          className="mb-6 p-4 rounded-md bg-[rgba(226,75,74,0.15)] border border-[rgba(226,75,74,0.4)] text-[#FCA5A5] text-sm"
        >
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>{STRINGS.auth.reset.invalidTokenError}</span>
          </div>
        </div>
        <Link
          to="/auth/forgot"
          className="block w-full h-12 rounded-lg bg-dream-gold text-dream-navy text-base font-semibold flex items-center justify-center"
        >
          {STRINGS.auth.reset.requestNewLink}
        </Link>
        <div className="mt-4 text-center">
          <Link
            to="/auth/login"
            className="text-sm text-dream-white/80 underline hover:text-dream-white"
          >
            {STRINGS.auth.forgot.backToLogin}
          </Link>
        </div>
      </>
    );
  }

  const formErrorMessage =
    formError === 'network'
      ? STRINGS.auth.errors.networkError
      : formError === 'generic'
        ? STRINGS.auth.errors.genericBackend
        : null;

  return (
    <>
      <h1 className="text-[22px] font-semibold text-dream-white mb-2">
        {STRINGS.auth.reset.title}
      </h1>
      <p className="text-sm text-dream-white/80 mb-6 leading-relaxed">
        {STRINGS.auth.reset.description}
      </p>

      {formErrorMessage && (
        <div
          role="alert"
          className="flex items-start gap-2 mb-4 p-3 rounded-md bg-[rgba(226,75,74,0.15)] border border-[rgba(226,75,74,0.4)] text-[#FCA5A5] text-sm"
        >
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{formErrorMessage}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="reset-password" className="text-sm text-dream-white">
            {STRINGS.auth.reset.passwordLabel}
          </Label>
          <div className="relative">
            <Input
              id="reset-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (passwordError) setPasswordError(null);
              }}
              onBlur={() => setPasswordError(validatePassword(password))}
              aria-invalid={Boolean(passwordError)}
              aria-describedby={passwordError ? 'reset-password-error' : undefined}
              className="h-11 pr-10 bg-dream-white text-dream-navy"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={
                showPassword ? STRINGS.auth.login.hidePassword : STRINGS.auth.login.showPassword
              }
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-dream-navy transition-colors bg-dream-white"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {passwordError ? (
            <p id="reset-password-error" className="text-xs text-[color:var(--color-error)]">
              {passwordError}
            </p>
          ) : (
            <PasswordStrengthMeter password={password} />
          )}
          <PasswordRequirementsChecklist password={password} tone="dark" />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="reset-password-confirm" className="text-sm text-dream-white">
            {STRINGS.auth.reset.passwordConfirmLabel}
          </Label>
          <Input
            id="reset-password-confirm"
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            value={passwordConfirm}
            onChange={(e) => {
              setPasswordConfirm(e.target.value);
              if (passwordConfirmError) setPasswordConfirmError(null);
            }}
            onBlur={() => setPasswordConfirmError(validateConfirm(passwordConfirm, password))}
            aria-invalid={Boolean(passwordConfirmError)}
            aria-describedby={passwordConfirmError ? 'reset-password-confirm-error' : undefined}
            className="h-11 bg-dream-white text-dream-navy"
          />
          {passwordConfirmError && (
            <p
              id="reset-password-confirm-error"
              className="text-xs text-[color:var(--color-error)]"
            >
              {passwordConfirmError}
            </p>
          )}
        </div>

        <Button
          type="submit"
          disabled={isSubmitting}
          className="w-full h-12 text-base font-semibold"
        >
          {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
          {isSubmitting ? STRINGS.auth.reset.loadingState : STRINGS.auth.reset.submitButton}
        </Button>
      </form>

      <div className="mt-6 text-center">
        <Link
          to="/auth/login"
          className="text-sm text-dream-white/80 underline hover:text-dream-white"
        >
          {STRINGS.auth.forgot.backToLogin}
        </Link>
      </div>
    </>
  );
}
